import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, Sprout, TreePine, Scissors, Wrench, Droplets, Shovel, ArrowUpFromLine, CloudRain } from "lucide-react";
import { services, companyInfo } from "../mock";

const iconMap = { Sprout, TreePine, Scissors, Wrench, Droplets, Shovel, ArrowUpFromLine, CloudRain };

export default function ServizioDettaglio() {
  const { id } = useParams();
  const service = services.find((s) => s.id === id);
  if (!service) return <Navigate to="/servizi" replace />;
  const Icon = iconMap[service.icon];
  const otherServices = services.filter((s) => s.id !== id).slice(0, 3);

  return (
    <div>
      {/* HERO */}
      <section className="relative pt-32 pb-0">
        <div className="relative h-[70vh] min-h-[500px] overflow-hidden bg-sage-900">
          {service.rotate ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src={service.image}
                alt={service.title}
                className="max-w-none"
                style={{
                  width: "min(70vh, 100vw)",
                  height: "auto",
                  transform: "rotate(90deg)",
                }}
              />
            </div>
          ) : (
            <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-sage-900/55" />
          <div className="absolute bottom-0 left-0 right-0 h-3/4 bg-gradient-to-t from-sage-900 via-sage-900/80 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto w-full px-6 lg:px-10 pb-14">
              <Link to="/servizi" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-8">
                <ArrowLeft className="w-4 h-4" /> Tutti i servizi
              </Link>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
                  {Icon && <Icon className="w-6 h-6 text-sage-700" strokeWidth={1.5} />}
                </div>
                <span className="text-xs tracking-[0.3em] uppercase text-white/80">Servizio</span>
              </div>
              <h1 className="font-serif text-4xl md:text-6xl text-white leading-[1.05] max-w-4xl">
                {service.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-14">
            <div className="lg:col-span-2 reveal">
              <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Descrizione</span>
              <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mt-4 mb-8 leading-tight">
                {service.description}
              </h2>
              <div className="text-lg text-sage-800/80 leading-relaxed space-y-5">
                <p>{service.longDescription}</p>
                <p>
                  Ogni intervento inizia con un sopralluogo gratuito e un ascolto attento delle tue esigenze. Studiamo poi la soluzione più adatta, forniamo un preventivo dettagliato e concordiamo insieme tempi e modalità di intervento.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-sage-900 mt-12 mb-6">Cosa include</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {service.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 p-4 bg-sage-50 rounded">
                    <CheckCircle2 className="w-5 h-5 text-sage-600 mt-0.5 shrink-0" strokeWidth={2} />
                    <span className="text-sm text-sage-900">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sidebar CTA */}
            <aside className="lg:col-span-1">
              <div className="sticky top-28 bg-sage-800 text-white p-8 rounded shadow-lg">
                <div className="text-xs tracking-[0.3em] uppercase text-sage-200 mb-3">Preventivo gratuito</div>
                <h3 className="font-serif text-3xl mb-4 leading-tight">Parliamone.</h3>
                <p className="text-sm text-white/80 leading-relaxed mb-6">
                  Contattaci per un sopralluogo senza impegno. Ti risponderemo entro 24 ore lavorative.
                </p>
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
                  className="flex items-center gap-3 mb-3 px-5 py-3.5 bg-white text-sage-800 rounded-full text-sm hover:bg-sage-50 transition-all"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.8} /> {companyInfo.phone}
                </a>
                <Link
                  to="/contatti"
                  className="flex items-center gap-3 px-5 py-3.5 border border-white/40 rounded-full text-sm hover:bg-white/10 transition-all"
                >
                  <ArrowRight className="w-4 h-4" strokeWidth={1.8} /> Modulo contatti
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* EXTRA GALLERY */}
      {service.extraImages && service.extraImages.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="mb-10 reveal">
              <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Portfolio</span>
              <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mt-3">Alcuni dei nostri lavori</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {service.extraImages.map((img, i) => (
                <div key={i} className="relative w-full h-[420px] overflow-hidden rounded shadow-md bg-sage-100 reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                  {img.rotate ? (
                    Math.abs(img.rotateDeg || 90) === 180 ? (
                      <img
                        src={img.url}
                        alt="Lavoro realizzato"
                        className="w-full h-full object-cover"
                        style={{ transform: `rotate(${img.rotateDeg}deg)` }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={img.url}
                          alt="Lavoro realizzato"
                          className="max-w-none"
                          style={{ height: "380px", width: "auto", transform: `rotate(${img.rotateDeg || 90}deg)` }}
                        />
                      </div>
                    )
                  ) : (
                    <img src={img.url} alt={img.caption || "Lavoro realizzato"} className="w-full h-full object-cover" />
                  )}
                  {img.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-5 pt-10 pb-4">
                      <p className="font-serif text-lg text-white">{img.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OTHER SERVICES */}
      <section className="py-24 bg-sage-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 reveal">
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Altri servizi</span>
              <h2 className="font-serif text-3xl md:text-4xl text-sage-900 mt-4">Scopri di più</h2>
            </div>
            <Link to="/servizi" className="text-sage-700 link-underline text-sm hidden sm:inline-flex items-center gap-2">
              Tutti i servizi <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherServices.map((s) => {
              const OIcon = iconMap[s.icon];
              return (
                <Link key={s.id} to={`/servizi/${s.id}`} className="group bg-white rounded overflow-hidden hover:shadow-xl transition-all duration-500">
                  <div className="h-56 overflow-hidden bg-sage-100">
                    {s.rotate ? (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <img src={s.image} alt={s.title} className="max-w-none w-auto h-[22rem] transition-transform duration-700 group-hover:scale-110" style={{ transform: "rotate(90deg)" }} />
                      </div>
                    ) : (
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    )}
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 mb-3">
                      {OIcon && <OIcon className="w-5 h-5 text-sage-700" strokeWidth={1.5} />}
                      <span className="text-xs tracking-[0.2em] uppercase text-sage-600">Servizio</span>
                    </div>
                    <h3 className="font-serif text-xl text-sage-900 mb-3">{s.title}</h3>
                    <p className="text-sm text-sage-700/70">{s.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
